import React, { useState, useEffect, useCallback } from 'react';
import Header from "./Header";
import parse from "html-react-parser";
import allQuotes from "./quotes.json";
import hallucinatedQuotes from "./hallucinated_quotes.json";
import PropTypes from "prop-types";
import './BenOrBot.css';

const backgroundsCount = 16;
const QUESTIONS_PER_ROUND = 10;

export const generateQuestions = (count = QUESTIONS_PER_ROUND) => {
    const usedBenIndices = new Set();
    const usedBotIndices = new Set();
    const questions = [];

    for (let i = 0; i < count; i++) {
        let source = Math.random() < 0.5 ? 'BEN' : 'BOT';

        if (source === 'BEN' && usedBenIndices.size >= allQuotes.length) {
            source = 'BOT';
        } else if (source === 'BOT' && usedBotIndices.size >= hallucinatedQuotes.length) {
            source = 'BEN';
        }

        if (source === 'BEN') {
            const available = [];
            for (let idx = 0; idx < allQuotes.length; idx++) {
                if (!usedBenIndices.has(idx)) available.push(idx);
            }
            const chosenIdx = available[Math.floor(Math.random() * available.length)];
            usedBenIndices.add(chosenIdx);
            questions.push({
                text: allQuotes[chosenIdx],
                source: 'BEN',
                id: `ben-${chosenIdx}`
            });
        } else {
            const available = [];
            for (let idx = 0; idx < hallucinatedQuotes.length; idx++) {
                if (!usedBotIndices.has(idx)) available.push(idx);
            }
            const chosenIdx = available[Math.floor(Math.random() * available.length)];
            usedBotIndices.add(chosenIdx);
            questions.push({
                text: hallucinatedQuotes[chosenIdx],
                source: 'BOT',
                id: `bot-${chosenIdx}`
            });
        }
    }

    return questions;
};

export const getResultMessage = (score, total = QUESTIONS_PER_ROUND) => {
    if (score === total) {
        return "Perfection! You know Ben better than Ben knows himself.";
    }
    if (score >= 8) {
        return "Outstanding! You can spot genuine Ben a mile off.";
    }
    if (score >= 5) {
        return "Good effort! But the AI is getting dangerously convincing.";
    }
    if (score >= 3) {
        return "Fooled by the bots! Ben's wisdom is an enigma.";
    }
    return "Total hallucination! Better brush up on your Ben lore.";
};

export default function BenOrBot(props) {
    const [questions, setQuestions] = useState(() => generateQuestions());
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedChoice, setSelectedChoice] = useState(null);
    const [score, setScore] = useState(0);
    const [roundFinished, setRoundFinished] = useState(false);
    const [bgIndex, setBgIndex] = useState(() => Math.floor(Math.random() * backgroundsCount));
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        document.title = 'Ben or Bot? - Wheelie Ben';
        document.description = 'Can you distinguish genuine Wheelie Ben quotes from AI hallucinations?';
    }, []);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleChoice = useCallback((choice) => {
        if (selectedChoice !== null || roundFinished) return;

        setSelectedChoice(choice);
        const currentQ = questions[currentIndex];
        if (choice === currentQ.source) {
            setScore(prev => prev + 1);
        }
    }, [selectedChoice, roundFinished, questions, currentIndex]);

    const handleNext = useCallback(() => {
        if (currentIndex < questions.length - 1) {
            setCurrentIndex(prev => prev + 1);
            setSelectedChoice(null);
            setBgIndex(prev => (prev + 1) % backgroundsCount);
        } else {
            setRoundFinished(true);
        }
    }, [currentIndex, questions.length]);

    const handleRestart = useCallback(() => {
        setQuestions(generateQuestions());
        setCurrentIndex(0);
        setSelectedChoice(null);
        setScore(0);
        setRoundFinished(false);
        setBgIndex(prev => (prev + 1) % backgroundsCount);
    }, []);

    // Keyboard controls for convenience
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (roundFinished) {
                if (e.key === 'Enter' || e.key === ' ') {
                    handleRestart();
                }
                return;
            }

            if (selectedChoice === null) {
                if (e.key === '1' || e.key === 'b' || e.key === 'B' || e.key === 'ArrowLeft') {
                    handleChoice('BEN');
                } else if (e.key === '2' || e.key === 'o' || e.key === 'O' || e.key === 'ArrowRight') {
                    handleChoice('BOT');
                }
            } else {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowRight') {
                    handleNext();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [roundFinished, selectedChoice, handleChoice, handleNext, handleRestart]);

    const currentBg = `/images/bg-${(bgIndex % backgroundsCount) + 1}.jpg`;
    const currentQ = questions[currentIndex];
    const isLongQuote = currentQ ? currentQ.text.length > 110 : false;
    const quoteStyle = {
        fontSize: (isLongQuote && isMobile) ? "2em" : "3em",
    };

    const hasAnswered = selectedChoice !== null;
    const isCorrect = hasAnswered && selectedChoice === currentQ.source;

    return (
        <div className="wrap ben-or-bot-wrap">
            <div 
                className="background-blur" 
                style={{ backgroundImage: `url('${currentBg}')` }}
            />
            <div 
                className="background-main" 
                style={{ backgroundImage: `url('${currentBg}')` }}
            />

            <Header navOpen={props.navOpen} toggleNav={props.toggleNav} page="Ben or Bot" />

            <div className="container game-container">
                {!roundFinished ? (
                    <>
                        <div className="game-header-info">
                            <span className="game-progress-badge">
                                Question {currentIndex + 1} / {QUESTIONS_PER_ROUND}
                            </span>
                            <span className="game-score-badge">
                                Score: {score}
                            </span>
                        </div>

                        <div className="Quote col-sm-offset-3 col-sm-6 col-md-offset-2 col-md-8 quote game-quote-box" style={quoteStyle}>
                            {parse(currentQ.text)}
                        </div>

                        <div className="game-controls">
                            <div className="game-options-row">
                                <button
                                    type="button"
                                    className={`game-btn-choice ${
                                        hasAnswered
                                            ? currentQ.source === 'BEN'
                                                ? 'correct-reveal'
                                                : selectedChoice === 'BEN'
                                                    ? 'incorrect-selected'
                                                    : 'dimmed'
                                            : ''
                                    }`}
                                    onClick={() => handleChoice('BEN')}
                                    disabled={hasAnswered}
                                >
                                    BEN
                                    <span className="btn-subtext">Genuine Ben</span>
                                </button>

                                <button
                                    type="button"
                                    className={`game-btn-choice ${
                                        hasAnswered
                                            ? currentQ.source === 'BOT'
                                                ? 'correct-reveal'
                                                : selectedChoice === 'BOT'
                                                    ? 'incorrect-selected'
                                                    : 'dimmed'
                                            : ''
                                    }`}
                                    onClick={() => handleChoice('BOT')}
                                    disabled={hasAnswered}
                                >
                                    BOT
                                    <span className="btn-subtext">AI Generated</span>
                                </button>
                            </div>

                            {hasAnswered && (
                                <div className="game-feedback-card">
                                    <div className={`feedback-status ${isCorrect ? 'is-correct' : 'is-incorrect'}`}>
                                        {isCorrect ? 'Correct!' : 'Incorrect!'}
                                    </div>
                                    <div className="feedback-details">
                                        {currentQ.source === 'BEN' 
                                            ? 'This was genuinely said by Ben.' 
                                            : 'This was hallucinated by AI.'}
                                    </div>
                                    <button 
                                        type="button" 
                                        className="game-btn-next" 
                                        onClick={handleNext}
                                        autoFocus
                                    >
                                        {currentIndex === questions.length - 1 ? 'See Final Score' : 'Next Quote'}
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="progress-bar-container page-furniture">
                            <div 
                                className="progress-bar" 
                                style={{ width: `${((currentIndex + (hasAnswered ? 1 : 0)) / QUESTIONS_PER_ROUND) * 100}%` }}
                            />
                        </div>
                    </>
                ) : (
                    <div className="Quote col-sm-offset-3 col-sm-6 col-md-offset-2 col-md-8 quote results-card">
                        <h2 className="results-title">Round Complete</h2>
                        <div className="results-score-box">
                            <div className="results-score-label">Your Final Score</div>
                            <div className="results-score-value">{score} / {QUESTIONS_PER_ROUND}</div>
                        </div>
                        <p className="results-message">
                            {getResultMessage(score, QUESTIONS_PER_ROUND)}
                        </p>
                        <button 
                            type="button" 
                            className="game-btn-restart" 
                            onClick={handleRestart}
                            autoFocus
                        >
                            Play Again
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

BenOrBot.propTypes = {
    navOpen: PropTypes.bool.isRequired,
    toggleNav: PropTypes.func.isRequired
};
